import type { GraphCsvUploadMapping } from "../api";
import type { CSVUploadFormData } from "../types/form-inputs";

import { client } from "../api/client.gen";

// Parse only the headers (first line) of a CSV text
export const parseCSVHeaders = (csvText: string): string[] => {
  const firstLine = csvText.split("\n")[0];
  return firstLine
    .split(",")
    .map((header) => header.trim().replace(/^["']|["']$/g, ""));
};

// Transform form data to API mapping format used by the backend
export const transformToApiMapping = (
  data: CSVUploadFormData,
): GraphCsvUploadMapping => {
  const columns: GraphCsvUploadMapping["columns"] = {};

  data.columnsMappings.forEach((mapping) => {
    const ignoreColumn = mapping.ignore === "true";
    if (ignoreColumn) {
      columns[mapping.name] = { ignore: true };
    } else if (mapping.entityQualifiedName) {
      columns[mapping.name] = {
        entityQualifiedName: mapping.entityQualifiedName,
        ignore: false,
      };
    } else {
      throw new Error(
        "Invalid column mapping: entityQualifiedName is required",
      );
    }
  });

  const relationships = data.relationships.map((rel) => {
    const sourceMapping = data.columnsMappings.find(
      (m) => m.entityQualifiedName === rel.fromEntityQualifiedName,
    );
    const targetMapping = data.columnsMappings.find(
      (m) => m.entityQualifiedName === rel.toEntityQualifiedName,
    );

    if (!sourceMapping || !targetMapping) {
      throw new Error(
        "Invalid relationship mapping: source or target not found",
      );
    }

    return {
      source: {
        column: sourceMapping.name,
        entityQualifiedName: rel.fromEntityQualifiedName,
      },
      target: {
        column: targetMapping.name,
        entityQualifiedName: rel.toEntityQualifiedName,
      },
      type: rel.name,
    };
  });

  return { columns, relationships };
};

// Perform the multipart upload and return a job id if the upload starts an async job
export const uploadCsvAndGetJobId = async (
  graphId: string,
  file: File,
  mapping: GraphCsvUploadMapping,
): Promise<null | string> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("mapping", JSON.stringify(mapping));

  const { auth, baseUrl } = client.getConfig();
  const apiBase = baseUrl || "http://localhost:8080";

  const response = await fetch(`${apiBase}/workspaces/${graphId}/upload`, {
    body: formData,
    headers: {
      ...(auth && { Authorization: `Bearer ${auth}` }),
    },
    method: "POST",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed: ${response.status} ${errorText}`);
  }

  const result = (await response.json()) as { id?: string; jobId?: string };
  return result.jobId || result.id || null;
};
